import React, { useCallback, useRef } from "react";
import moment from "moment";
import classNames from "classnames";
import PreviewImages, { PreviewImagesRef } from "../PreviewImages";
import styles from "./index.module.scss";

type DataFormat =
  | "YYYY/MM/DD HH:mm:ss"
  | "YYYY/MM/DD HH:mm"
  | "YYYY/MM/DD"
  | "YYYY/MM"
  | "YYYY";

export interface InfoDisplayColumn {
  /**
   * 项标题
   */
  title: string | ((value: any, data: any, index: number) => React.ReactNode);
  /**
   * 数据字段，对应 dataSource 中的属性名
   */
  dataIndex: string;
  /**
   * 数据渲染类型，默认为 text
   */
  type?: "text" | "time" | "image";
  /**
   * 自定义渲染内容
   * @param value
   * @param data
   * @returns
   */
  render?: (value: any, data: any, index: number) => React.ReactNode;
  /**
   * 时间格式化，默认为 YYYY/MM/DD HH:mm，type 为 time时的有效
   */
  dateFormat?: DataFormat & string;
  /**
   * 图片列表数据，且优先级高于 dataIndex 字段取值，type 为 image 时的有效
   */
  photoList?: string[];
  /**
   * 后缀，可以补充一些单位，如 CM、L、KM、岁等等，type 为 text 时的有效
   */
  suffix?: string | React.ReactNode;
  /**
   * 该项是否可见，默认为 true，即可见
   */
  visible?: boolean | ((value: any, data: any, index: number) => boolean);
}

interface InfoDisplayProps {
  /**
   * 列配置
   */
  columns: InfoDisplayColumn[];
  /**
   * 数据源
   */
  dataSource: any;
  /**
   * 空值填充内容，默认为空字符串
   */
  emptyFill?: string;
}

/**
 * 判断数据是否为空，包括 null、undefined、空字符串
 * @param data
 * @returns
 */
const isNil = (data: any): boolean => {
  return data === null || data === undefined || data === "";
};

/**
 * 信息展示组件
 * @param props
 * @returns
 */
const InfoDisplay = (props: InfoDisplayProps) => {
  const { columns, dataSource, emptyFill = "" } = props;
  const previewRef = useRef<PreviewImagesRef>(null);

  const renderValue = useCallback(
    (column: InfoDisplayColumn, index: number) => {
      const {
        type = "text",
        dataIndex,
        dateFormat = "YYYY/MM/DD HH:mm",
        render,
        suffix = "",
      } = column;
      let value: string | React.ReactNode = "";
      switch (type) {
        case "time":
          value = isNil(dataSource?.[dataIndex])
            ? emptyFill
            : moment(dataSource?.[dataIndex]).format(dateFormat);
          break;
        default:
          value = isNil(dataSource?.[dataIndex])
            ? emptyFill
            : String(dataSource?.[dataIndex]) + suffix;
          break;
      }
      return typeof render === "function"
        ? render?.(dataSource?.[dataIndex], dataSource, index)
        : value;
    },
    [dataSource, emptyFill],
  );

  return (
    <div className={styles.infoDisplayComp}>
      {Array.isArray(columns) &&
        columns.map((column, index) => {
          const {
            title,
            visible = true,
            photoList,
            dataIndex,
            type = "text",
          } = column;
          const _visible =
            typeof visible === "function"
              ? visible(dataSource?.[dataIndex], dataSource, index)
              : visible;
          if (!_visible) return null;
          const _photoList = Array.isArray(photoList)
            ? photoList
            : Array.isArray(dataSource?.[dataIndex])
              ? dataSource?.[dataIndex]
              : [];
          const _title =
            typeof title === "function"
              ? title(dataSource?.[dataIndex], dataSource, index)
              : title;
          const isPhotoType = type === "image";
          return (
            <div
              key={`${title}-${dataIndex}`}
              className={classNames({
                [styles.infoDisplayPhotoItem]: isPhotoType,
                [styles.infoDisplayItem]: !isPhotoType,
              })}
            >
              {!!_title && <div className={styles.label}>{_title}</div>}
              {isPhotoType ? (
                <div className={styles.photoSection}>
                  {_photoList.map((url, _index) => (
                    <div
                      onClick={() =>
                        previewRef.current?.open({
                          index: _index,
                          urls: _photoList,
                        })
                      }
                      key={`${_index.toString()}`}
                      className={styles.imageItem}
                    >
                      <img src={url} alt="" />
                    </div>
                  ))}
                </div>
              ) : (
                // 非图片展示
                <div className={styles.value}>{renderValue(column, index)}</div>
              )}
            </div>
          );
        })}
      <PreviewImages ref={previewRef} />
    </div>
  );
};

export default InfoDisplay;
